<template>
  <Article
    v-if="post"
    :post="post"
    :display-shortened-content="
      $store.getters['paywall/displayShortenedContent']
    "
    :is-free="post.isFree"
    role="main"
    :page-settings="pageSettings"
    :class="specialClasses"
  />
</template>

<script>
import {
  enhance,
  generateOpenGraphMeta,
  generateLdPlusJson,
} from '~plugins/utils'
import { entityDecode } from '~~/utils/entity-decode.js'
import Article from '~components/content/detail-view/Article.vue'

export default {
  name: 'SinglePostPage',
  components: {
    Article,
  },
  async asyncData({ error, app, params, route, redirect }) {
    try {
      const post = await app.apolloProvider.defaultClient
        .query({
          query: require('~base/graphql/queries/SingleBySlug.gql'),
          variables: {
            post_slug: params.post_slug.replace(/\.(vn|neue)$/, ''),
          },
        })
        .then(({ data }) => data.post && enhance(data.post, app.$config))
      if (post === null) {
        if (app.$config.redirect404) {
          redirect(`${app.$config.redirect404}${route.fullPath}`)
        } else {
          error({ statusCode: 404 })
        }
      }
      return { post }
    } catch (e) {
      if (process.browser && !navigator.onLine) {
        error({ statusCode: 200, message: 'offline' })
      } else {
        error({ statusCode: 404 })
      }
    }
  },
  data() {
    return {
      post: {},
      sentToPiano: false,
      pageSettings: undefined,
    }
  },
  head() {
    const head = {
      title: this.post
        ? `${entityDecode(this.post.title)} - ${this.$config.siteName}`
        : '...',
      meta: this.post ? this.meta : [],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: this.canonical,
        },
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: generateLdPlusJson(this.$config, this.post),
    }
    return head
  },
  computed: {
    specialClasses() {
      return this.post.isStory ? `story-content` : ''
    },
    meta() {
      const meta = generateOpenGraphMeta(this.$config, this.post)
      meta.push(this.robots)
      return meta
    },
    postCategory() {
      const [c] = this.post.relevantCategories
      return c
    },
    canonical() {
      if (!this.post) return undefined
      if (this.post.canonical) return this.post.canonical
      return this.post.permalink
    },
    robots() {
      const robots = {
        hid: 'robots',
        name: 'robots',
        content: 'index, follow, noarchive',
      }
      if (
        this.post.categories.nodes.find(
          (c) => c.slug === 'vol' || c.slug === 'eliteliga'
        )
      ) {
        robots.content = 'noindex, follow, noarchive'
      }
      if (this.post.tags.nodes.find((c) => c.slug === 'grund-und-boden')) {
        if (new Date(this.post.dateGmt) > new Date('2022-01-20T00:00:00')) {
          robots.content = 'noindex, follow, noarchive'
        }
      }

      if (this.post.tags.nodes.find((c) => c.slug === 'gastro-tipp')) {
        if (new Date(this.post.dateGmt) > new Date('2022-06-22T00:00:00')) {
          robots.content = 'noindex, follow, noarchive'
        }
      }
      return robots
    },
  },
  created() {
    const categories = this.post.relevantCategories.map((c) => c.slug)
    const tags = this.post.tags?.nodes.map((t) => t.slug)

    const categorySettings = this.$store.getters['news/categorySettings']

    const catFound = categories.find((c) =>
      Object.prototype.hasOwnProperty.call(categorySettings, c)
    )
    if (catFound) {
      this.pageSettings = categorySettings[catFound]
      return
    }

    const tagsSettings = this.$store.getters['news/tagsSettings']
    const tagFound = tags.find((t) =>
      Object.prototype.hasOwnProperty.call(tagsSettings, t)
    )

    if (tagFound) {
      this.pageSettings = tagsSettings[tagFound]
    }
  },
  mounted() {
    window.scrollTo(0, 0)
    this.$store.dispatch('ads/pageChanged', {
      pageType: 'article',
      category: this.postCategory?.slug || '',
    })
    if (!this.sentToPiano) {
      this.$store.dispatch('paywall/executeExperience', this.post)
      this.sentToPiano = true
    }
  },
}
</script>

<style>
.default-layout > main > div.story-content {
  @apply -mt-4 lg:max-w-full;
}
</style>
